// One "Restart agent" run as the backend reports it (GatewayRecoveryView).
export type RecoveryRun = {
  id: string;
  stage: "restarting" | "rebooting" | "done";
  outcome: "restarted" | "rebooted" | "failed" | null;
  rebooted: boolean;
  startedAt: string;
  finishedAt: string | null;
};

export type RecoveryCopy = {
  title: string;
  body: string;
  finished: boolean;
};

export const POLL_INTERVAL_MS = 4000;

// The latest run, or null (204) when the tenant has none.
export const fetchLatestRun = async (url: string, signal: AbortSignal): Promise<RecoveryRun | null> => {
  const res = await fetch(url, { credentials: "include", signal });
  if (res.status === 204) return null;
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as RecoveryRun;
};

// The run this modal shows: the one it started (refreshed by polling), or one
// already in flight when the modal opened. A finished run from earlier is not shown.
export const shownRun = (latest: RecoveryRun | null, started: RecoveryRun | null): RecoveryRun | null => {
  if (started !== null) return latest?.id === started.id ? latest : started;
  return latest !== null && latest.stage !== "done" ? latest : null;
};

// What the modal says at each step. The reboot step is spelled out because
// it takes noticeably longer and the customer should know why.
export const recoveryCopy = (run: RecoveryRun, name: string): RecoveryCopy => {
  if (run.stage === "restarting") {
    return {
      title: "Restarting your agent",
      body: `Restarting ${name}. This usually takes a minute or two. You can close this window, the restart carries on.`,
      finished: false,
    };
  }
  if (run.stage === "rebooting") {
    return {
      title: "Restarting the server",
      body: `A normal restart didn't get ${name} going, so we're restarting the server it runs on. This takes a few minutes.`,
      finished: false,
    };
  }
  if (run.outcome === "failed") {
    return {
      title: "Your agent is still down",
      body: `We couldn't bring ${name} back automatically. Our team has been alerted and will look into it.`,
      finished: true,
    };
  }
  return {
    title: "Your agent is back",
    body: run.rebooted
      ? `${name} is back online. We restarted its server to get it going. Your conversations, settings and connections are all kept.`
      : `${name} is back online. Your conversations, settings and connections are all kept.`,
    finished: true,
  };
};
